/**
 * ConfirmAttachmentModal — in-app bottom-sheet confirmation shown when the user
 * taps Send with an attachment but no accompanying text. Replaces the native
 * `window.confirm()` used in PR #1135 (issue #1137).
 *
 * Pattern: matches `ModelControlSheet` — backdrop + slide-up sheet + drag handle
 * + close (✕) header button. Reuses the existing `.mcs-*` CSS classes so
 * theming (sand / Zen / Moss / Slate) and motion are automatic and consistent
 * with the rest of the app's bottom sheets (per UI rule R-005 in
 * `voice-assistant/docs/ui-ux-runbook.md`).
 *
 * Accessibility:
 * - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` for screen readers
 * - Visible close (✕) button in header (R-013: 48dp tap target via CSS padding)
 * - Explicit Cancel + Send buttons (no implicit OK/Cancel ambiguity)
 * - Autofocus on the Cancel button — safer default than Send (the whole point
 *   of this guard is that the user may have hit Send by mistake), and matches
 *   native confirm() behaviour where Cancel is the default.
 *
 * No hardcoded colors (R-004): all surfaces, text, borders use semantic CSS
 * variables defined in `style.css` (`--surface-warm`, `--text-primary`, etc.).
 */
import { useEffect, useRef } from 'preact/hooks';

interface Props {
  /** Whether the sheet is open */
  open: boolean;
  /** User confirmed — proceed with sending the attachment-only message */
  onConfirm: () => void;
  /** User cancelled — close the sheet, do not send */
  onCancel: () => void;
}

export function ConfirmAttachmentModal({ open, onConfirm, onCancel }: Props) {
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  // Move focus to the Cancel button when the sheet opens — matches the
  // native `confirm()` default-to-Cancel behaviour and protects against an
  // accidental Enter-key from sending the empty-text message.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => cancelBtnRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Escape key dismisses the sheet (matches native confirm() semantics).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      class="mcs-backdrop"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-attach-title"
    >
      <div
        class="mcs-sheet confirm-attach-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (same affordance as ModelControlSheet) */}
        <div class="mcs-handle-bar" />

        {/* Header — title + close button (✕, 48dp tap target via .mcs-close padding) */}
        <div class="mcs-header">
          <span id="confirm-attach-title" class="mcs-title">Send without text?</span>
          <button
            class="mcs-close"
            onClick={onCancel}
            aria-label="Close confirmation"
            type="button"
          >
            ✕
          </button>
        </div>

        {/* Body copy explains the guard */}
        <div class="confirm-attach-body">
          <p class="confirm-attach-message">
            You're about to send the attachment with no text.
          </p>
          <p class="confirm-attach-help">
            Tap <strong>Cancel</strong> if you meant to dictate, or <strong>Send</strong> to share the attachment as-is.
          </p>
        </div>

        {/* Action row — Cancel (secondary) + Send (primary) */}
        <div class="confirm-attach-actions">
          <button
            ref={cancelBtnRef}
            class="confirm-attach-btn confirm-attach-btn--cancel"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            class="confirm-attach-btn confirm-attach-btn--send"
            onClick={onConfirm}
            type="button"
          >
            Send anyway
          </button>
        </div>

        <div class="mcs-safe-bottom" />
      </div>
    </div>
  );
}
